import type { Product, ScheduleTime } from '@src/core/buyerApiClient';
import { extractLimitFrom } from '@src/domains/tariffs';
import { getSelectedTariff } from './tariffLookup';
import type { TariffsPerType, TariffsState } from '@src/stores/product';

export function getSelectedTime(selectedHour: ScheduleTime | undefined): number | undefined {
  return selectedHour?.minutes;
}

export function acceptableQuantities(
  tariffsPerType: TariffsPerType,
  tariffsInformation: TariffsState,
  product: Pick<Product, 'minSaleTariffQuantity' | 'maxSaleTariffQuantity'>,
  selectedHour: ScheduleTime | undefined,
): [number, number] {
  const range = extractLimitFrom(tariffsInformation, tariffsPerType, selectedHour?.minutes);
  const saleTariffQuantities: [number, number] = [product.minSaleTariffQuantity, product.maxSaleTariffQuantity];

  if (range && saleTariffQuantities) {
    const min = saleTariffQuantities[0];
    const max = Math.min(saleTariffQuantities[1], range[1]);
    return [min, max];
  }

  return saleTariffQuantities ?? range ?? [0, 0];
}

export function getTariffDescription(tariffPerType: TariffsPerType, selectedHour: ScheduleTime | undefined): string {
  const minutesSelected = selectedHour ? selectedHour.minutes : null;
  const tariff = getSelectedTariff(tariffPerType, minutesSelected);
  return tariff && tariff.description ? tariff.description : '';
}

export function isNotPossibleToSelect(typeId: string, listOfTariffsPerType: TariffsPerType[]): boolean {
  const tariffsPerType = listOfTariffsPerType.find((tariffPerType) => tariffPerType.type.id === typeId);
  if (!tariffsPerType) return false;

  for (const scheduling of tariffsPerType.schedulings) {
    const tariff = scheduling.tariffGroup.tariffs.find((t) => t.type.id === typeId);
    if (!tariff || !scheduling.occupationRates.tariffQuantities) continue;

    const tariffOccupation = scheduling.occupationRates.tariffQuantities.find((tq) => tq.tariffId === tariff.id);
    if (
      tariffOccupation &&
      tariffOccupation.occupation.availableAmount != null &&
      tariffOccupation.occupation.availableAmount < tariffsPerType.selectedQuantity
    ) {
      return true;
    }
  }

  return false;
}

export function isTariffDependent(
  typeId: string,
  listOfTariffsPerType: TariffsPerType[],
  selectedHour: ScheduleTime | undefined,
): boolean {
  for (const tariffPerType of listOfTariffsPerType) {
    if (tariffPerType.type.id === typeId) {
      const tariff = getSelectedTariff(tariffPerType, selectedHour ? selectedHour.minutes : null);
      return !!tariff?.isDependent;
    }
  }
  return false;
}

export function isDependentTariffAndIsSelected(
  typeId: string,
  listOfTariffsPerType: TariffsPerType[],
  selectedHour: ScheduleTime | undefined,
): boolean {
  const tariffType = listOfTariffsPerType.find((t) => typeId === t.type.id);
  return (
    !!tariffType && tariffType.selectedQuantity > 0 && isTariffDependent(typeId, listOfTariffsPerType, selectedHour)
  );
}

export function calculateSelectedTariffIndex(
  sortedTariffs: readonly { selectedQuantity: number }[],
  currentTariffIndex: number,
): number | null {
  let sequentialIndex = 0;
  for (let i = 0; i <= currentTariffIndex; i++) {
    const tariff = sortedTariffs[i];
    if (tariff && tariff.selectedQuantity > 0) {
      if (i === currentTariffIndex) {
        return sequentialIndex;
      }
      sequentialIndex++;
    }
  }
  return null;
}
